import { z } from 'zod';
import { PasswordSchema } from './auth.schema.js';
import { HHMM_RE } from './settings.schema.js';
import { salaryTotalAddsUp, SALARY_TOTAL_ISSUE } from './salaryTotal.js';

// MALE / FEMALE only. `OTHER` was removed on the owner's instruction
// (2026-08-11). Also narrows LeaveType.applicableGender, which is this same
// enum — an eligibility of OTHER would have matched nobody.
export const GenderSchema = z.enum(['MALE', 'FEMALE']);
export const MaritalStatusSchema = z.enum(['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED']);
export const ContractTypeSchema = z.enum(['LIMITED', 'UNLIMITED', 'PROBATION']);
export const EmploymentStatusSchema = z.enum([
  'ACTIVE',
  'PROBATION',
  'NOTICE_PERIOD',
  'RESIGNED',
  'TERMINATED',
  'ON_LEAVE',
]);
// OWN = our payroll; AGENCY = external manpower agency worker (track hours,
// bill back to the supplier — never paid via payroll, never in WPS).
export const EmploymentTypeSchema = z.enum(['OWN', 'AGENCY']);

/**
 * "Attendance access" — two INDEPENDENT axes (mirrors Prisma AttendanceLocation
 * / AttendanceMethod). The old single AttendanceMode enum conflated them.
 *
 * Location — WHERE a worker may punch (self-enforces on the web punch path).
 * Three modes, in escalating order of freedom, because the company has three
 * populations and two modes forced two of them into the wrong box:
 *   ASSIGNED_SITE — must be inside a site this worker is ASSIGNED to
 *   ANY_WORKSITE  — must be inside ANY active company site; nearest wins and
 *                   the punch is attributed to it. Assignment is a default,
 *                   not a fence. Still fenced — that is the point: labour
 *                   follows the job across projects, and per-project hours
 *                   only mean something if they were really standing there.
 *   ANYWHERE      — no site requirement at all (outside-radius flagged, GPS
 *                   optional), bounded only by the operating region
 * Method — HOW a worker authenticates:
 *   APP_LOGIN  — self check-in in the web/mobile app
 *   KIOSK_FACE — face at a mounted kiosk; no web self-punch
 *   FIELD_FACE — face via the handheld field app; no web self-punch
 */
export const AttendanceLocationSchema = z.enum([
  'ASSIGNED_SITE',
  'ANY_WORKSITE',
  'ANYWHERE',
]);
export type AttendanceLocation = z.infer<typeof AttendanceLocationSchema>;
export const AttendanceMethodSchema = z.enum([
  'APP_LOGIN',
  'KIOSK_FACE',
  'FIELD_FACE',
]);
export type AttendanceMethod = z.infer<typeof AttendanceMethodSchema>;

/**
 * "Attendance access" as ONE value (mirrors Prisma `AttendanceProfile`), which
 * is what the two axes above always added up to.
 *
 * The pair was never a 3×3 grid. Location does not govern a face punch, so it
 * only ever expressed five states — and the employee form has been collapsing it
 * to exactly those five since the axes were split, in order to write a sentence
 * an admin could read. Two of the five differ solely by which device took the
 * photo, and the device identifies itself on every punch, so that belongs on the
 * event rather than on the person. Five becomes four, and the combinations that
 * never meant anything stop being representable.
 *
 *   SELF_ASSIGNED — signs in; must be inside a worksite they are ASSIGNED to
 *   SELF_ANY_SITE — signs in; must be inside ANY active company worksite, nearest
 *                   wins. Not geofence-lenient — still standing on a site
 *   SELF_ANYWHERE — signs in; no worksite requirement, outside-radius tolerated
 *                   and FLAGGED. Still bounded by OPERATING_REGION (geofence)
 *   FACE_DEVICE   — does not self-punch; recorded by face at a kiosk or handheld,
 *                   and the device supplies the location
 *
 * Orthogonal to Role (what you may SEE) and Designation (job title — "Foreman"
 * is a designation and grants nothing).
 */
export const AttendanceProfileSchema = z.enum([
  'SELF_ASSIGNED',
  'SELF_ANY_SITE',
  'SELF_ANYWHERE',
  'FACE_DEVICE',
]);
export type AttendanceProfile = z.infer<typeof AttendanceProfileSchema>;

/**
 * The old pair -> the stored value. Twin of the CASE in the
 * `20260815120000_attendance_profile` migration; change them together or a
 * backfilled row and a freshly-written one stop agreeing.
 *
 * Method is tested FIRST and this is load-bearing, not stylistic. Location does
 * not govern a face punch, so a worker sitting on the ASSIGNED_SITE default while
 * being recorded at a kiosk has to land on FACE_DEVICE. Reading location first
 * would quietly turn every kiosk worker into a self-punching one.
 */
export function attendanceProfileFromAxes(
  location: AttendanceLocation,
  method: AttendanceMethod
): AttendanceProfile {
  if (method !== 'APP_LOGIN') return 'FACE_DEVICE';
  if (location === 'ANYWHERE') return 'SELF_ANYWHERE';
  if (location === 'ANY_WORKSITE') return 'SELF_ANY_SITE';
  return 'SELF_ASSIGNED';
}

/**
 * The stored value -> the two deprecated columns, so they stay truthful for the
 * few places that still READ them: the live board's "what does where mean for
 * this row", the calendar's remote filter, and one report column. Those are
 * display and grouping concerns with their own DTO field names, locale keys and
 * Excel headers, so they are migrated separately — until then, a form that sends
 * only a profile must not leave them frozen at whatever they were.
 *
 * Exists ONLY until those columns are dropped. Delete it with them.
 *
 * The FACE_DEVICE direction is the lossy one: a profile cannot say whether the
 * capture device is a mounted kiosk or a handheld, because that distinction was
 * deliberately moved onto the punch. `currentMethod` is therefore honoured when
 * it is already a face method, so editing an unrelated field on a kiosk worker
 * does not quietly relabel them as field-captured in the report column. Only a
 * worker who had no face method at all falls back to FIELD_FACE.
 */
export function legacyAxesFromProfile(
  profile: AttendanceProfile,
  currentMethod?: AttendanceMethod
): { attendanceLocation: AttendanceLocation; attendanceMethod: AttendanceMethod } {
  if (profile === 'FACE_DEVICE') {
    return {
      attendanceLocation: 'ASSIGNED_SITE',
      attendanceMethod:
        currentMethod === 'KIOSK_FACE' || currentMethod === 'FIELD_FACE'
          ? currentMethod
          : 'FIELD_FACE',
    };
  }
  return {
    attendanceMethod: 'APP_LOGIN',
    attendanceLocation:
      profile === 'SELF_ANYWHERE'
        ? 'ANYWHERE'
        : profile === 'SELF_ANY_SITE'
          ? 'ANY_WORKSITE'
          : 'ASSIGNED_SITE',
  };
}
// Per-worker FIELD face-enrollment workflow state (mirrors Prisma
// FieldEnrollmentStatus). Surfaced on the employee DTOs and as a list filter so
// the web can show a "pending enrollment" list/badge.
export const EmployeeEnrollmentStatusSchema = z.enum([
  'NOT_ENROLLED',
  'PENDING_SYNC',
  'ENROLLED',
]);

/**
 * First-class workforce classification (mirrors Prisma `WorkforceType`). This is
 * the single source of truth the "Site workers" pillar switch, the roster
 * group-filter and the report scope all key off — replacing the old inference
 * from `userId == null` / `attendanceMethod` / `employeeCode` prefix.
 *   SITE_WORKER  — login-less field/site worker (face/kiosk attendance)
 *   OFFICE_STAFF — login-holding staff
 */
export const WorkforceTypeSchema = z.enum(['SITE_WORKER', 'OFFICE_STAFF']);
export type WorkforceType = z.infer<typeof WorkforceTypeSchema>;

/**
 * Row-level READ authority for ONE person (mirrors Prisma `AccessScope`).
 *
 * Scope used to be derived from Role, and Role could only say "manager" — which
 * `resolveReportScope` turned into `{ managerId: self }`, i.e. DIRECT reports
 * and nothing else. A department head with two team leads under them saw the
 * two leads and not the sixty people the leads manage. Widening the MANAGER
 * rule for everyone would have given every front-line supervisor the same
 * reach, so the answer became a per-person fact.
 *
 *   SELF           — their own record only
 *   DIRECT_REPORTS — employees whose `managerId` is this person (today's
 *                    MANAGER behaviour)
 *   DEPARTMENT     — their own department; with `accessIncludeSubDepartments`,
 *                    the whole subtree beneath it
 *   COMPANY        — the whole roster
 *
 * Orthogonal to Role (what you may DO) and to AttendanceProfile (how you are
 * recorded). Role still acts as a ceiling in one direction — ADMIN / HR are
 * company-wide regardless — and only ADMIN / HR may WRITE this field.
 */
export const AccessScopeSchema = z.enum([
  'SELF',
  'DIRECT_REPORTS',
  'DEPARTMENT',
  'COMPANY',
]);
export type AccessScope = z.infer<typeof AccessScopeSchema>;

/**
 * The scope a person gets when nobody states one — derived from the role being
 * granted, so a create lands on the SAME value the upgrade backfill gave the
 * people who already had that role.
 *
 * Twin of the CASE in the `20260815140000_employee_access_scope` migration.
 * Change them together, or a backfilled row and a freshly-written one stop
 * agreeing about what "a manager" can see.
 *
 * A login-less worker (no role at all) gets SELF: they never sign in, so there
 * is no report for them to read, and SELF is the value that grants nothing.
 */
export function defaultAccessScopeForRole(
  role: 'ADMIN' | 'HR' | 'MANAGER' | 'EMPLOYEE' | null | undefined
): AccessScope {
  if (role === 'ADMIN' || role === 'HR') return 'COMPANY';
  if (role === 'MANAGER') return 'DIRECT_REPORTS';
  return 'SELF';
}

const optionalDate = z
  .union([z.string().datetime(), z.string().date(), z.literal('')])
  .optional()
  .nullable()
  .transform((v) => (v ? v : null));

// UAE Emirates ID: 784-YYYY-NNNNNNN-N (15 digits with hyphen separators).
// Enforced for every payload that flows through EmployeeCreateSchema /
// EmployeeUpdateSchema — i.e. the create/edit form and the public REST API.
// The helpers below are the single source of truth: the bulk-import row
// validator (validateRow in employeeBulkImport.service.ts) imports the SAME
// isValidEmiratesId/normalizeEmiratesId so all three entry points validate and
// canonicalize identically. Empty string and null pass through (optional field).
// Hyphens are optional on input so "784..." with or without separators is
// accepted; valid values are normalized to the canonical dashed form on output.
export const EMIRATES_ID_REGEX = /^784-?\d{4}-?\d{7}-?\d$/;

/** True when `value` is a structurally valid UAE Emirates ID (dashed or not). */
export function isValidEmiratesId(value: string): boolean {
  return EMIRATES_ID_REGEX.test(value);
}

/**
 * Canonicalize a valid Emirates ID to the dashed form `784-YYYY-NNNNNNN-N`.
 * Strips all non-digits then re-inserts separators. The caller must have already
 * confirmed `isValidEmiratesId(value)` (this does not re-validate).
 */
export function normalizeEmiratesId(value: string): string {
  const d = value.replace(/\D/g, '');
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7, 14)}-${d.slice(14, 15)}`;
}

const emiratesIdNumber = z
  .string()
  .max(40)
  .refine((v) => v === '' || isValidEmiratesId(v), {
    message: 'Emirates ID must be in the format 784-YYYY-NNNNNNN-N',
  })
  .transform((v) => (v === '' ? v : normalizeEmiratesId(v)))
  .optional()
  .nullable();

// IBAN basic structure check (not the full mod-97 checksum): 2 letters + 2
// check digits + up to 30 alphanumerics. UAE IBANs are 23 chars (AE + 21
// digits) but we keep it generic so foreign IBANs validate too. Spaces are
// tolerated by stripping before the regex test; empty/null pass through.
const IBAN_REGEX = /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/;
const bankIban = z
  .string()
  .max(40)
  .refine(
    (v) => {
      const compact = v.replace(/\s+/g, '').toUpperCase();
      return compact === '' || IBAN_REGEX.test(compact);
    },
    {
      message:
        'IBAN must start with a 2-letter country code and 2 check digits (e.g. AE07…)',
    }
  )
  .optional()
  .nullable();

/**
 * Per-worker working START time — "HH:MM" in the company timezone, or null.
 *
 * This is an OVERRIDE of `settings.defaultWorkday.shiftStartTime`, not a copy:
 * the rollup resolves the start as
 * `employee.workStartTime ?? defaultWorkday.shiftStartTime`, so null means "use
 * whatever the company default is TODAY" and moving the company start still
 * moves everybody who has not been given their own. That is why null has to stay
 * reachable — clearing the field is how a worker is handed back to the company
 * rule, and a schema that could only ever set a time would strand every worker
 * who was ever given one.
 *
 * It exists for the night crew on a tenant that runs no shift module: with one
 * company-wide start of 07:00 a 22:00 check-in reads as 900 minutes late every
 * night. Stored as "22:00" here, the same punch is on time.
 *
 * Empty string normalises to null rather than failing, because that is what an
 * `<input type="time">` submits when the admin CLEARS it — the same reason
 * `optionalDate` above transforms `''`. Rejecting it would turn "hand this
 * worker back to the company default" into a validation error.
 *
 * The regex is the SHARED strict `HHMM_RE` from settings.schema, deliberately
 * imported rather than retyped: the loose `\d{1,2}:\d{2}` accepts "25:99", which
 * reaches `combineDateAndZonedTime` and throws, turning one bad row into a
 * failing rollup for the whole company.
 */
const workStartTime = z
  .union([z.string().regex(HHMM_RE, 'Expected HH:MM'), z.literal('')])
  .optional()
  .nullable()
  .transform((v) => (v ? v : null));

/**
 * Per-worker working END time. Identical rules to `workStartTime` above — same
 * strict HHMM_RE, same empty-string-to-null transform so CLEARING the input
 * hands the worker back to the company default rather than failing validation,
 * and the same override-not-copy semantics.
 *
 * It is what makes leaving early measurable. Lateness has always been the first
 * check-in against the start; without an end there was no matching question to
 * ask of the last check-out, so a worker who left three hours early was
 * indistinguishable from one who worked a short day by arrangement.
 *
 * Deliberately does NOT move a pay figure — see `defaultWorkday.shiftEndTime`.
 */
const workEndTime = z
  .union([z.string().regex(HHMM_RE, 'Expected HH:MM'), z.literal('')])
  .optional()
  .nullable()
  .transform((v) => (v ? v : null));

// The raw object shape, kept separate from EmployeeCreateSchema (which wraps it
// in a .refine, producing a ZodEffects that no longer exposes .partial/.omit).
// EmployeeUpdateSchema derives from THIS so its field-level shape stays intact.
const employeeCreateObject = z
  .object({
    // Login credentials are OPTIONAL: construction "workers" are login-less
    // (Employee.userId is nullable). When `email` is omitted/empty the employee
    // is created with no User row (no password/role either). The refine below
    // enforces that email+password travel together so we never create a User
    // with a missing credential. `role` defaults to EMPLOYEE but is ignored on
    // the login-less path.
    email: z.string().email().toLowerCase().optional().nullable().or(z.literal('')),
    password: PasswordSchema.optional().nullable().or(z.literal('')),
    phone: z.string().max(40).optional().nullable(),

  /**
   * THE COMPANY'S OWN NUMBER FOR THIS PERSON, and optional because the server
   * still generates one when it is absent.
   *
   * It used to be server-only: `generateEmployeeCode()` minted `EMP-` plus six
   * random hex and no request could say otherwise, so a company that already
   * numbers its people — which, at a thousand head with an ERP, is all of them
   * — could not enter the number their own paperwork uses. The code is on the
   * roster, the record and every export warning, so a random one is a column
   * nobody recognises.
   *
   * Empty string is accepted and means "generate one", because that is what an
   * untouched form field sends — the same shape `email` uses two fields down.
   * The character set is deliberately loose (`LB01968`, `EMP-123`, `A/12`,
   * `4471` are all real formats) and only excludes whitespace and the
   * punctuation that would make a CSV or a filename ambiguous.
   *
   * Uniqueness is NOT expressed here: it is a database constraint and a query,
   * and a zod schema cannot see the other rows. The service checks it and
   * catches the race — see `resolveEmployeeCode`.
   */
  employeeCode: z
    .string()
    .trim()
    .max(32)
    .regex(
      /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
      'Code may use letters, digits, dot, dash, slash and underscore, and must start with a letter or digit'
    )
    .optional()
    .nullable()
    .or(z.literal('')),

  firstName: z.string().min(1).max(80),
  lastName: z.string().min(1).max(80),
  middleName: z.string().max(80).optional().nullable(),
  displayName: z.string().max(160).optional().nullable(),

  dateOfBirth: optionalDate,
  gender: GenderSchema.optional().nullable(),
  nationality: z.string().max(80).optional().nullable(),
  maritalStatus: MaritalStatusSchema.optional().nullable(),

  personalEmail: z.string().email().optional().nullable().or(z.literal('')),
  emergencyName: z.string().max(120).optional().nullable(),
  emergencyPhone: z.string().max(40).optional().nullable(),
  emergencyRelation: z.string().max(80).optional().nullable(),
  addressLine1: z.string().max(160).optional().nullable(),
  addressLine2: z.string().max(160).optional().nullable(),
  city: z.string().max(80).optional().nullable(),
  emirate: z.string().max(80).optional().nullable(),
  country: z.string().max(80).optional().nullable(),

  passportNumber: z.string().max(40).optional().nullable(),
  passportExpiry: optionalDate,
  emiratesIdNumber,
  emiratesIdExpiry: optionalDate,
  visaNumber: z.string().max(40).optional().nullable(),
  visaExpiry: optionalDate,
  laborCardNumber: z.string().max(40).optional().nullable(),
  laborCardExpiry: optionalDate,

  // The ERP payroll this person is paid on — the CLOCKCARDSBATCH `PRL` column.
  // A STRING, deliberately: the real values are "002"/"003"/"004"/"006" and the
  // leading zero is part of the value, so this must never be coerced to a number
  // (z.coerce.number() would silently turn "002" into 2 and break the batch).
  // Nullable because NULL is meaningful — "not on any ERP payroll", i.e. the
  // workers the export excludes and lists on screen — so it has to stay
  // clearable, not just settable.
  erpPayrollNumber: z.string().max(20).optional().nullable(),

  joiningDate: z.union([z.string().datetime(), z.string().date()]),
  confirmationDate: optionalDate,
  probationEndDate: optionalDate,
  contractType: ContractTypeSchema.default('LIMITED'),
  contractStartDate: optionalDate,
  contractEndDate: optionalDate,
  employmentType: EmploymentTypeSchema.default('OWN'),
  // Worker vs Staff. OPTIONAL and WITHOUT a `.default()`, and both of those are
  // load-bearing: when the caller omits it the service still derives it from the
  // login signal (login-less -> SITE_WORKER, with-login -> OFFICE_STAFF), and a
  // schema default would make the value always present post-parse, silently
  // killing that derivation and turning every future login-less worker into
  // OFFICE_STAFF. Supplying it is the explicit override — and, via the update
  // schema below, the only way to correct a record created as the wrong type
  // (the derivation runs once at creation and was never revisitable).
  // Not nullable: the Prisma column is NOT NULL, so there is nothing to clear to.
  workforceType: WorkforceTypeSchema.optional(),
  legalEntityId: z.string().optional().nullable(),
  supplierId: z.string().optional().nullable(),
  departmentId: z.string().optional().nullable(),
  designationId: z.string().optional().nullable(),
  managerId: z.string().optional().nullable(),
  employmentStatus: EmploymentStatusSchema.default('ACTIVE'),
  // How this person is recorded. Sent by the current form as ONE value; the two
  // axes below are what an older client sends instead, and the service falls
  // back to deriving from them when this is absent. Deliberately WITHOUT a
  // `.default()`: a default would make the field always present post-parse, and
  // the service could no longer tell "the client chose SELF_ASSIGNED" apart from
  // "the client is old and did not send this at all" — which is exactly the
  // distinction the fallback turns on.
  attendanceProfile: AttendanceProfileSchema.optional(),
  /** @deprecated send `attendanceProfile`; accepted so older clients keep working. */
  attendanceLocation: AttendanceLocationSchema.default('ASSIGNED_SITE'),
  /** @deprecated send `attendanceProfile`; accepted so older clients keep working. */
  attendanceMethod: AttendanceMethodSchema.default('APP_LOGIN'),
  // The local rule that beats the company one — see the `workStartTime` const
  // above. Deliberately WITHOUT a `.default()`: a default would make the value
  // always present post-parse and every worker would carry a private copy of
  // whatever the company start was on the day they were saved.
  workStartTime,
  workEndTime,

  // Row-level READ authority. OPTIONAL and WITHOUT a `.default()`, for exactly
  // the reason `workforceType` above is: when the caller omits it the service
  // derives it from the role being granted (`defaultAccessScopeForRole`), and a
  // schema default would make the value always present post-parse, silently
  // killing that derivation and filing every new manager as SELF.
  //
  // Not nullable — the Prisma column is NOT NULL, so there is nothing to clear
  // to. Present on the UPDATE schema too (it derives from this object), which is
  // the only way to promote a team lead to a department head; "settable on
  // create but not on update" has been a real defect in this file before.
  //
  // Reaching this field is NOT enough to set it: `mayAssignAccessScope` in
  // employees.routes.ts rejects the request unless the CALLER is ADMIN / HR, so
  // a manager cannot widen their own authority by patching their own record.
  accessScope: AccessScopeSchema.optional(),
  // Only meaningful alongside `accessScope: 'DEPARTMENT'`. Optional and
  // default-less on the same grounds: absent means "leave whatever is stored
  // alone", which is what stops a form that does not carry the checkbox from
  // quietly collapsing a department head back to their own department.
  accessIncludeSubDepartments: z.boolean().optional(),

  basicSalary: z.number().nonnegative().optional().nullable(),
  housingAllowance: z.number().nonnegative().optional().nullable(),
  transportAllowance: z.number().nonnegative().optional().nullable(),
  otherAllowance: z.number().nonnegative().optional().nullable(),
  totalSalary: z.number().nonnegative().optional().nullable(),
  bankName: z.string().max(120).optional().nullable(),
  bankAccountNumber: z.string().max(40).optional().nullable(),
  bankIban,

  role: z.enum(['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE']).default('EMPLOYEE'),

  // Consent for the ONE side effect a `managerId` write can have on a record
  // this request never opens: promoting the new manager from EMPLOYEE to
  // MANAGER. Optional and default-less on purpose — omitted (the common case,
  // every save that doesn't touch `managerId`) must not read as "confirmed",
  // so the service treats "not `true`" as "not confirmed" rather than reading
  // a schema default as consent nobody gave. See
  // employee.service.ts's syncManagerRole and
  // ApiError.managerPromotionRequired.
  confirmManagerPromotion: z.boolean().optional(),
  });

export const EmployeeCreateSchema = employeeCreateObject
  // email and password must travel together. Both absent → login-less worker
  // (no User row). Both present → a normal staff login. One without the other
  // is rejected so we never half-create a credential.
  .refine(
    (v) => {
      const hasEmail = !!v.email && v.email !== '';
      const hasPassword = !!v.password && v.password !== '';
      return hasEmail === hasPassword;
    },
    { message: 'Email and password must both be provided to create a login account', path: ['email'] }
  )
  .refine(salaryTotalAddsUp, SALARY_TOTAL_ISSUE); // M-08 — see salaryTotal.ts
export type EmployeeCreate = z.infer<typeof EmployeeCreateSchema>;
// Pre-parse (input) shape: defaulted fields (`role`, …) may be omitted — the
// login-less worker path drops email/password/role.
export type EmployeeCreateInput = z.input<typeof EmployeeCreateSchema>;

// Update: the same fields without password/email (separate flows), optional —
// except the identifiers the UI marks `required` (P2-5). Self-update is the
// EmployeeSelfUpdateSchema whitelist below. `accessRoleId` is the form's one
// Role field, written in this save under the People tab's rules
// (AccessRoleService.planAssign); it is never sent together with `role`.
export const EmployeeUpdateSchema = employeeCreateObject.partial()
  .omit({ email: true, password: true })
  .merge(
    z.object({
      firstName: z.string().min(1).max(80),
      lastName: z.string().min(1).max(80),
      joiningDate: z.union([z.string().datetime(), z.string().date()]),
      accessRoleId: z.string().min(1).nullable().optional(),
    })
  )
  .refine(salaryTotalAddsUp, SALARY_TOTAL_ISSUE); // M-08 — see salaryTotal.ts
export type EmployeeUpdate = z.infer<typeof EmployeeUpdateSchema>;

// What employees may edit on themselves.
export const EmployeeSelfUpdateSchema = z.object({
  middleName: z.string().max(80).optional().nullable(),
  displayName: z.string().max(160).optional().nullable(),
  personalEmail: z.string().email().optional().nullable().or(z.literal('')),
  emergencyName: z.string().max(120).optional().nullable(),
  emergencyPhone: z.string().max(40).optional().nullable(),
  emergencyRelation: z.string().max(80).optional().nullable(),
  addressLine1: z.string().max(160).optional().nullable(),
  addressLine2: z.string().max(160).optional().nullable(),
  city: z.string().max(80).optional().nullable(),
  emirate: z.string().max(80).optional().nullable(),
  country: z.string().max(80).optional().nullable(),
});
export type EmployeeSelfUpdate = z.infer<typeof EmployeeSelfUpdateSchema>;

/*
 * The roster's list query lives in `employeeQuery.schema.ts` and is NOT
 * re-exported from here: that module imports four enums from this one, and an
 * `export … from` is hoisted above the module body, so the re-export made the
 * enums read in their temporal dead zone and the API could not boot. Consumers
 * take it from the package root, which exports both files side by side.
 * `importCycles.test.ts` holds the full account and guards the shape.
 */

export const DocumentTypeSchema = z.enum([
  'PASSPORT',
  'EMIRATES_ID',
  'VISA',
  'LABOR_CARD',
  'CONTRACT',
  'OFFER_LETTER',
  'NDA',
  'POLICY_ACK',
  'EDUCATION',
  'CERTIFICATION',
  'CV',
  'PHOTO',
  'MEDICAL',
  'OTHER',
]);

export const EmployeeDocumentInputSchema = z.object({
  type: DocumentTypeSchema,
  name: z.string().min(1).max(160),
  issueDate: optionalDate,
  expiryDate: optionalDate,
  notes: z.string().max(2000).optional().nullable(),
});
export type EmployeeDocumentInput = z.infer<typeof EmployeeDocumentInputSchema>;
